import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import ProductOrderManagement from './pages/product-order-management';
import LoginAuthentication from './pages/login-authentication';
import UserManagementDashboard from './pages/user-management-dashboard';
import StatisticsDashboard from './pages/statistics-dashboard';
import SharedFile from './pages/shared-file';
import ModuloOrdineRivenditorePage from './pages/modulo-ordine-rivenditore';
import { useAuth } from './contexts/AuthContext';

// Protected Route component - enforces authentication
const ProtectedRoute = ({ children, adminOnly = false, fallbackPath = "/login" }) => {
  const { isAuthenticated, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // ENFORCE LOGIN - redirect to login if not authenticated
  if (!isAuthenticated) {
    window.location.href = fallbackPath;
    return null;
  }

  // Admin check for admin-only routes
  if (adminOnly && userProfile?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Accesso Negato</h2>
          <p className="text-gray-600 mb-6">
            Questa sezione richiede privilegi di amministratore.
          </p>
          <div className="space-y-3">
            <a
              href="/product-order-management"
              className="block w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Vai al Modulo Ordini
            </a>
            <a
              href="/login"
              className="block w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cambia Account
            </a>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Login route - accessible without authentication */}
          <Route path="/login" element={<LoginAuthentication />} />
          <Route path="/login-authentication" element={<LoginAuthentication />} />
          <Route path="/" element={<ModuloOrdineRivenditorePage />} />
          <Route path="/modulo-ordine-rivenditore" element={<ModuloOrdineRivenditorePage />} />
          <Route path="/shared-file/:fileId" element={<SharedFile />} />
          
          {/* PROTECTED ROUTES - require authentication */}
          <Route 
            path="/product-order-management" 
            element={
              <ProtectedRoute>
                <ProductOrderManagement />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin-only routes */}
          <Route 
            path="/user-management-dashboard" 
            element={
              <ProtectedRoute adminOnly={true}>
                <UserManagementDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/statistics-dashboard" 
            element={
              <ProtectedRoute adminOnly={true}>
                <StatisticsDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Fallback route */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
